import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowRight, FileClock, FileText, FolderOpen, Inbox, Mail, ShieldCheck, Sparkles, Truck, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useFeatures } from '@/lib/features';
import { LearnFromDocumentsDialog } from '../learning/learn-dialog';
import { GreenInvoiceCard } from '../settings/green-invoice-card';
import { pickDriveFiles } from './drive-picker';
import { setupApi, type SetupStatus } from './setup-api';

/** קובץ הדמו ש-IMPORT_FAKE_GOOGLE מכיר (fake-mailbox.ts בשרת). השם מתורגם ברינדור. */
const DEMO_DRIVE_FILE = { id: 'demo-pricelist', name: 'demo-pricelist.csv' };

export function ConnectGoogleStep({ status }: { status: SetupStatus }) {
  const { t } = useTranslation(['onboarding', 'common']);
  const errorText = (err: unknown): string => (err instanceof Error ? err.message : t('common:errors.generic'));
  const fileName = (f: { id: string; name: string }) => (f.id === DEMO_DRIVE_FILE.id ? t('connect.demoFile') : f.name);
  const toast = useToast();
  const qc = useQueryClient();
  const connected = status.google.gmail === 'CONNECTED';
  // בלי הרשאת קריאת מייל (שלב 1 של אימות Google) הייבוא מ-Google הוא רק מקבצי Drive.
  const readsMail = connected ? status.google.mailRead : status.google.mailReadAvailable;
  const giConnected = status.greenInvoice === 'CONNECTED';
  // מספיק מקור אחד: Google, חשבונית ירוקה, או שניהם.
  const canStart = connected || giConnected;
  const [files, setFiles] = useState<Array<{ id: string; name: string }>>(
    status.fakeGoogle ? [DEMO_DRIVE_FILE] : [],
  );

  const connect = useMutation({
    mutationFn: setupApi.connectGoogle,
    // יוצאים ל-Google; ה-callback בשרת מחזיר ל-/onboarding?connected=google.
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
    onError: (err: unknown) =>
      toast.error(
        t('connect.openFailed'),
        err instanceof ApiError && err.status === 503
          ? t('connect.notConfigured')
          : errorText(err),
      ),
  });

  const pick = useMutation({
    mutationFn: async () => {
      const config = await setupApi.picker();
      if (!config.enabled) throw new Error(t('connect.pickerNotConfigured'));
      return pickDriveFiles(config);
    },
    onSuccess: (picked) =>
      setFiles((prev) =>
        [...prev, ...picked.filter((p) => !prev.some((f) => f.id === p.id))].slice(0, 30),
      ),
    onError: (err: unknown) => toast.error(t('connect.pickFailed'), errorText(err)),
  });

  const start = useMutation({
    mutationFn: () => setupApi.start(files.map((f) => f.id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-import'] }),
    onError: (err: unknown) => toast.error(t('connect.startFailed'), errorText(err)),
  });

  // בלי חיבורים — ממשיכים למערכת. כל חיבור אפשר להוסיף אחר כך בהגדרות ← חיבורים.
  const navigate = useNavigate();
  const skip = useMutation({
    mutationFn: setupApi.finish,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['onboarding-import'] });
      void navigate({ to: '/', replace: true });
    },
    onError: (err: unknown) => toast.error(t('connect.skipFailed'), errorText(err)),
  });

  // הלמידה מ-Google / חשבונית ירוקה בונה מחירון, לקוחות וסוגי עבודה. לעסק בלי
  // מחירון ובלי לקוחות (ניהול משרד) אין לה מה ללמוד — מציגים את מה שכן רלוונטי.
  const features = useFeatures();
  const learns = features.has('priceList') || features.has('customers');

  const failed = status.run?.status === 'FAILED' ? status.run : null;

  if (!learns) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('connect.office.title')}</CardTitle>
            <CardDescription>{t('connect.office.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="grid gap-3 text-sm sm:grid-cols-2">
              {features.has('suppliers') ? (
                <Benefit icon={<Truck aria-hidden />} title={t('connect.office.suppliers')}>
                  {t('connect.office.suppliersText')}
                </Benefit>
              ) : null}
              {features.has('contracts') ? (
                <Benefit icon={<FileClock aria-hidden />} title={t('connect.office.contracts')}>
                  {t('connect.office.contractsText')}
                </Benefit>
              ) : null}
              {features.has('supplierInvoices') ? (
                <Benefit icon={<Inbox aria-hidden />} title={t('connect.office.invoices')}>
                  {t('connect.office.invoicesText')}
                </Benefit>
              ) : null}
              <Benefit icon={<Mail aria-hidden />} title={t('connect.office.google')}>
                {t('connect.office.googleText')}
              </Benefit>
            </ul>
            <Button size="lg" className="w-full" loading={skip.isPending} onClick={() => skip.mutate()}>
              {t('connect.office.enter')}
              <ArrowRight className="rtl:-scale-x-100" aria-hidden />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {failed ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-xs text-danger"
        >
          <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
          <span>
            {failed.error
              ? t('connect.previousFailedReason', { error: failed.error })
              : t('connect.previousFailed')}
          </span>
        </div>
      ) : null}

      <Card>
        <CardHeader className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{t('connect.title')}</CardTitle>
            <CardDescription>
              {readsMail ? t('connect.descMail') : t('connect.descDrive')}
            </CardDescription>
          </div>
          {connected ? (
            <Badge tone="success" className="shrink-0 whitespace-nowrap">{status.fakeGoogle ? t('connect.demo') : t('connect.connected')}</Badge>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="grid gap-3 text-sm sm:grid-cols-3">
            {readsMail ? (
              <>
                <Benefit icon={<Mail aria-hidden />} title={t('connect.benefits.customers')}>
                  {t('connect.benefits.customersText')}
                </Benefit>
                <Benefit icon={<FileText aria-hidden />} title={t('connect.benefits.priceList')}>
                  {t('connect.benefits.priceListText')}
                </Benefit>
                <Benefit icon={<ShieldCheck aria-hidden />} title={t('connect.benefits.privacy')}>
                  {t('connect.benefits.privacyMailText')}
                </Benefit>
              </>
            ) : (
              <>
                <Benefit icon={<FileText aria-hidden />} title={t('connect.benefits.drivePriceList')}>
                  {t('connect.benefits.drivePriceListText')}
                </Benefit>
                <Benefit icon={<Mail aria-hidden />} title={t('connect.benefits.sendFromYou')}>
                  {t('connect.benefits.sendFromYouText')}
                </Benefit>
                <Benefit icon={<ShieldCheck aria-hidden />} title={t('connect.benefits.privacy')}>
                  {t('connect.benefits.privacyDriveText')}
                </Benefit>
              </>
            )}
          </ul>

          {!connected ? (
            <Button
              size="lg"
              className="w-full"
              loading={connect.isPending}
              onClick={() => connect.mutate()}
            >
              {t('connect.signIn')}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {connected ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('connect.driveTitle')}</CardTitle>
            <CardDescription>
              {t('connect.driveDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {files.length > 0 ? (
              <ul className="space-y-1.5">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-2 rounded-(--radius-md) border border-border bg-surface px-3 py-2 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <FileText className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                      <span dir="auto" className="truncate">
                        {fileName(f)}
                      </span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('connect.removeAria', { name: fileName(f) })}
                      onClick={() => setFiles((prev) => prev.filter((p) => p.id !== f.id))}
                    >
                      <X aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            {!status.fakeGoogle ? (
              <Button variant="secondary" loading={pick.isPending} onClick={() => pick.mutate()}>
                <FolderOpen aria-hidden />
                {t('connect.pickFiles')}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {features.has('invoicing') || giConnected ? <GreenInvoiceCard description={t('connect.greenInvoice')} /> : null}

      {/* בלי Google: מעלים הצעות מחיר / חשבוניות / מחירון, והאשף ממשיך להתקדמות ולאישור. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('connect.uploadTitle')}</CardTitle>
          <CardDescription>{t('connect.uploadDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <LearnFromDocumentsDialog
            triggerSize="md"
            onStarted={() => void qc.invalidateQueries({ queryKey: ['onboarding-import'] })}
          />
        </CardContent>
      </Card>

      {canStart ? (
        <Button
          size="lg"
          className="w-full"
          loading={start.isPending}
          onClick={() => start.mutate()}
        >
          <Sparkles aria-hidden />
          {t('connect.startLearning')}
        </Button>
      ) : null}

      {/* בלי Google ובלי חשבונית ירוקה — לא תקועים. */}
      <div className="flex flex-col items-center gap-1 pt-2 text-center">
        <Button variant={canStart ? 'ghost' : 'secondary'} size={canStart ? 'md' : 'lg'} className={canStart ? '' : 'w-full'} loading={skip.isPending} onClick={() => skip.mutate()}>
          {t('connect.skip')}
        </Button>
        <p className="text-xs text-fg-muted">{t('connect.skipHint')}</p>
      </div>
    </div>
  );
}

function Benefit({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="rounded-(--radius-md) border border-border bg-surface p-3">
      <div className="mb-1 flex items-center gap-2 font-medium text-fg [&_svg]:size-4 [&_svg]:text-accent">
        {icon}
        {title}
      </div>
      <p className="text-xs text-fg-muted">{children}</p>
    </li>
  );
}
